import { CommunityRoleBadge } from './community.models';

/**
 * Contract of the community profile endpoints (Api::CommunityProfileController, Phase 3):
 * the member's own profile with per-field visibility, the public view of another
 * member and personal blocks.
 */

/** Who may see a contact field: nobody | every member | only members sharing a group. */
export type CommunityVisibility = 'private' | 'network' | 'groups';
export const COMMUNITY_VISIBILITIES: readonly CommunityVisibility[] = ['private', 'network', 'groups'];

export interface CommunityOwnProfileUser {
  id: number;
  username: string | null;
  full_name: string | null;
  first_name: string | null;
  avatar_url: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
}

/** `GET /community/profile` */
export interface CommunityOwnProfile {
  user: CommunityOwnProfileUser;
  bio: string | null;
  calling: string | null;
  whatsapp: string | null;
  website: string | null;
  social: Record<string, string>;
  visibility: Record<string, CommunityVisibility>;
  /** Country/region the member chose for the profile — not the address. */
  country_code?: string | null;
  region?: string | null;
  /** The address, offered as a prefill for an empty country/region. */
  suggested?: { country_code: string | null; region: string | null };
  /** "Profiles connect people": short lists rendered as chips, each with its own visibility. */
  hobbies?: string[];
  interests?: string[];
  exchange_topics?: string[];
  /** Holds the `practitioner` role: the calling, the professional block and the two switches apply. */
  is_practitioner: boolean;
  practitioner_visible: boolean;
  practitioner_accepts_dm: boolean;
  /** The professional block: specialty, activity, focus, approach, philosophy, specializations, languages. */
  practitioner_details?: Record<string, string>;
  practitioner_detail_keys?: string[];
  limits: { bio: number; calling: number; list_items?: number; list_item?: number; detail?: number };
  social_keys: string[];
  contact_fields: string[];
  /** The list fields (hobbies, interests, exchange_topics) and every field with a visibility switch. */
  list_fields?: string[];
  profile_fields?: string[];
}

/** `PUT /community/profile` — every field optional (partial update). */
export interface CommunityProfileUpdate {
  bio?: string | null;
  calling?: string | null;
  whatsapp?: string | null;
  website?: string | null;
  social?: Record<string, string>;
  visibility?: Record<string, CommunityVisibility>;
  country_code?: string | null;
  region?: string | null;
  hobbies?: string[];
  interests?: string[];
  exchange_topics?: string[];
  practitioner_visible?: boolean;
  practitioner_accepts_dm?: boolean;
  practitioner_details?: Record<string, string>;
}

export interface CommunityPublicProfileUser {
  id: number;
  username: string | null;
  full_name: string | null;
  first_name: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  founding_member: boolean;
  founding_member_number: number | null;
  /** "Founding Practitioner" — the Practitioner Network's own honour, held on top
   *  of (never instead of) the Founding Member one. Absent on older backends. */
  founding_practitioner?: boolean;
  founding_practitioner_number?: number | null;
  badges: CommunityRoleBadge[];
  member_since: string;
}

/** Only the fields the member opened for the viewer are present. */
export interface CommunityPublicContact {
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  website?: string | null;
  social?: Record<string, string> | null;
  country?: string | null;
  region?: string | null;
  city?: string | null;
}

/** The lists the member opened for the viewer (absent = private). */
export interface CommunityPublicAbout {
  hobbies?: string[];
  interests?: string[];
  exchange_topics?: string[];
}

/** One row of the practitioner directory (`GET /community/practitioners`). */
export interface CommunityPractitionerEntry {
  id: number;
  username: string | null;
  full_name: string | null;
  first_name: string | null;
  avatar_url: string | null;
  founding_practitioner_number: number | null;
  country: string | null;
  region: string | null;
  calling: string | null;
  specialty: string | null;
  focus: string | null;
  languages: string | null;
  accepts_dm: boolean;
}

/** `GET /community/users/:id` */
export interface CommunityPublicProfile {
  user: CommunityPublicProfileUser;
  is_self: boolean;
  bio: string | null;
  /** Absent on older backends. */
  about?: CommunityPublicAbout;
  contact: CommunityPublicContact;
  practitioner: { calling: string | null; accepts_dm: boolean; details?: Record<string, string> } | null;
  shared_groups: string[];
  stats: { threads: number; posts: number };
  can_message: boolean;
  is_blocked: boolean;
}

export interface CommunityBlockedUser {
  id: number;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
}

/** Bases for social handles; a value that already is a URL is used as-is. */
const SOCIAL_BASE: Record<string, string> = {
  instagram: 'https://instagram.com/',
  facebook: 'https://facebook.com/',
  linkedin: 'https://linkedin.com/in/',
  telegram: 'https://t.me/',
  x: 'https://x.com/',
  youtube: 'https://youtube.com/',
  tiktok: 'https://tiktok.com/@'
};

/** Link for a social entry: full URLs pass through, bare handles get the network's base. */
export function socialUrl(key: string, value: string): string {
  const v = (value || '').trim();
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, '');
  return (SOCIAL_BASE[key] ?? '') + handle;
}

/** Display name for any user brief with `full_name` / `username` / `first_name`. */
export function profileDisplayName(u: { full_name?: string | null; username?: string | null; first_name?: string | null; id?: number } | null | undefined): string {
  if (!u) return '—';
  return u.full_name || u.first_name || u.username || (u.id != null ? `#${u.id}` : '—');
}
